import { withRef } from "./apiError";

/**
 * ENG-3449 — which answers to an invitation end it, and what to tell the person about the others.
 *
 * `useJoinInvitation` used to forget the saved invitation on every non-2xx and on a network error. But
 * `POST /tenant/accept-invite` (PremiumRoutes.kt) refuses several invitations it leaves `pending`,
 * and each of those can still be accepted once the person or an administrator does something:
 *
 * | Status | Error                                           | Invitation      | What to do                     |
 * |--------|-------------------------------------------------|-----------------|--------------------------------|
 * | 404    | Invitation not found                            | gone            | ask for a new one              |
 * | 410    | Invitation has expired                          | expired         | ask for a new one              |
 * | 400    | Invitation is no longer valid                   | revoked/accepted| nothing                        |
 * | 403    | This invitation was sent to X. Please sign in…  | still pending   | sign in as X                   |
 * | 400    | All seats are filled. Contact an administrator… | still pending   | an admin frees a seat          |
 * | 400    | No active subscription for this organization    | still pending   | an admin renews                |
 * | 409    | `already_on_plan`: a family and another plan    | still pending   | leave the other plan          |
 * | 5xx    | Failed to accept invite                         | still pending   | try again                      |
 *
 * Only the first three are TERMINAL. The two pending 400s share their status with a terminal one, so
 * they are told apart by their text. That match fails toward forgetting: if the backend rewords either,
 * it reads as terminal again, which is what every 400 did before this, rather than as a dead invitation
 * kept for a week and refused on every visit. A 400 `code` from the backend would make this exact.
 */

/** The server's answer to an invitation, in the terms the hook acts on. */
export interface JoinRefusal {
  /** No later attempt can succeed: forget the invitation. Otherwise it stays saved for another try. */
  terminal: boolean;
  /** For the person, saying what to do next when there is something to do. */
  message: string;
}

const SEATS_FULL = /seat/i;
const NO_SUBSCRIPTION = /subscription/i;

/** Whether the server's answer rules out every later attempt to accept this invitation. */
export function isTerminalInviteAnswer(status: number, body: unknown): boolean {
  if (status === 404 || status === 410) return true;
  if (status !== 400) return false;
  const error = errorText(body);
  return !(SEATS_FULL.test(error) || NO_SUBSCRIPTION.test(error));
}

/** A refused `POST /tenant/accept-invite`: whether it ends the invitation, and what to say. */
export function joinRefusal(status: number, body: unknown): JoinRefusal {
  const error = errorText(body);
  if (isTerminalInviteAnswer(status, body)) {
    return { terminal: true, message: error || "This invitation is no longer valid." };
  }
  if (status === 403) {
    const invited = (body as { invitedEmail?: unknown } | null)?.invitedEmail;
    const message = typeof invited === "string" && invited.trim() !== ""
      ? `This invitation was sent to ${invited}. Sign out, then sign in as ${invited} to join.`
      : "This invitation was sent to a different email address. Sign out, then sign in with that address to join.";
    return { terminal: false, message };
  }
  if (status === 409 && (body as { code?: unknown } | null)?.code === "already_on_plan" && error) {
    return { terminal: false, message: error };
  }
  if (status === 400 && SEATS_FULL.test(error)) {
    return {
      terminal: false,
      message: "All seats in this organization are filled. Ask one of its administrators to free a seat, then reload this page to join.",
    };
  }
  if (status === 400 && NO_SUBSCRIPTION.test(error)) {
    return {
      terminal: false,
      // ENG-3248: the server names a family as one, and only a family's Owner holds its billing (ENG-3246).
      message: /\bfamily\b/i.test(error)
        ? "This family's plan isn't active, so it can't add anyone. Ask the family's owner to renew it, then reload this page to join."
        : "This organization has no active subscription, so it can't add members. Ask one of its administrators to renew it, then reload this page to join.",
    };
  }
  return {
    terminal: false,
    message: withRef("Couldn't accept your invitation just now. Reload this page to try again.", (body as { ref?: unknown } | null)?.ref),
  };
}

/** The request never got an answer. The invitation is kept: nothing says it cannot be accepted. */
export const JOIN_UNREACHABLE =
  "Couldn't reach Haven to accept your invitation. Check your connection, then reload this page to try again.";

function errorText(body: unknown): string {
  const error = (body as { error?: unknown } | null)?.error;
  return typeof error === "string" ? error.trim() : "";
}
